import React, { useEffect, useMemo, useState } from 'react';
import { FieldProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import dynamic from 'next/dynamic';
import { Box, Button, DialogContent, Typography } from '@mui/material';
import {
  CardHcd,
  User,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { useCurrentUser } from '@docavenue/components';
import { getAddress, getCenterName } from '../../utils/card';

import CenterItem from '../CenterItem/CenterItem';
import { centersActions, practitionersActions } from '@/src/actions';
import { useTranslation } from '@/src/i18n';
import SearchProvider from '../../templates/Search/SearchProvider';
import List from '../../organisms/SearchResults/List';

import { analyticsEvent } from '@/src/analytic_tag_manager';
import Dialog from '@/components/molecules/Dialog/Dialog';
import { getPatientFullAddress } from '@/components/utils/patient';
import { ConsultationReasonPublicType } from '@/src/types/enums';
import { HOME_VISIT } from '@/src/constants';

const Map = dynamic(() => import('../Map/Map'), { ssr: false });

type Values = {
  consultationReasonName: string;
  rootCenterId: string;
  specialityId: string;
  centerId: string;
  type: ConsultationReasonPublicType;
  [key: string]: any;
};

interface Props extends FieldProps<any, Values> {
  card: CardHcd;
  cards: CardHcd[];
  onReset: () => any;
  type: string;
}

type Item = {
  center: {
    id: string;
  };
};

const CenterSelect: React.FC<Props> = ({
  field,
  form: { setFieldValue, values },
  card,
  cards,
  onReset,
  type,
  ...rest
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser<User>();
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [open, setOpen] = useState(false);

  const {
    consultationReasonName,
    rootCenterId,
    specialityId, // practitionerId, TODO: verify if select center is enabled for practitioner page
    centerId: currentCenterId,
    type: appointmentType,
  } = values;

  const centersPractitioner = cards.filter(practitioner =>
    practitioner?.consultationReasons?.some(
      reason => reason.name === consultationReasonName,
    ),
  );
  const isCenter = type === 'CENTER';
  const centersList = useSelector<any, CardHcd[]>(state =>
    get(state, 'centers.items', []),
  );
  const centers = (isCenter ? centersList : centersPractitioner).filter(
    (c: CardHcd) => {
      // In case center secret, it dont have child
      // so we allow it access directly by check center.id === rootCenterId
      if (c.type === 'CENTER' && c?.center?.id === rootCenterId) return true;
      return !c?.center?.isSecret;
    },
  );
  const centerSelected = useMemo(
    () => centers.find(c => c?.center?.id === currentCenterId),
    [centers, currentCenterId],
  );

  const handleClickOpen = () => {
    setOpen(true);
    analyticsEvent({
      category: 'Booking',
      action: 'ClickCentersList',
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isCenter) {
      dispatch(
        centersActions.getList({
          consultationReasonName,
          rootCenterId,
          specialityId,
        }),
      );
    }
  }, [consultationReasonName, rootCenterId]);

  useEffect(() => {
    if (isCenter && centers?.length) {
      dispatch(centersActions.setItem(centers[0]));
      setFieldValue(field.name, centers[0]?.center?.id);
      return;
    }
    dispatch(centersActions.setItem(card)); // Quick fix, TODO: review multiple center flow while implementing MPAT-1032
  }, [centers?.length]);

  const refItems = React.useMemo(
    () =>
      centers.map(item => ({
        ...item,
        ref: () => React.createRef(),
        markerRef: () => React.createRef(),
      })),
    [centers],
  );

  if (appointmentType === HOME_VISIT) {
    return (
      <>
        <div className="selected--item">
          <span>{t('your-home')}</span>
          <br />
          {currentUser ? (
            <>{getPatientFullAddress(undefined, currentUser)}</>
          ) : (
            t('log-in-to-access-this-information')
          )}
        </div>
        {currentUser && (
          <Typography variant="subtitle1" sx={{ fontStyle: 'italic' }}>
            {t('check-your-address-or-change-it')}
          </Typography>
        )}
      </>
    );
  }

  return (
    <>
      {centerSelected && (
        <div className="selected--item">
          <span>{`${getCenterName(centerSelected)}`}</span>
          <br />
          {getAddress(centerSelected)}
        </div>
      )}
      {centers.length > 1 && (
        <>
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              sx={{ px: 4, py: 0 }}
              color="primary"
              variant="outlined"
              onClick={handleClickOpen}
              data-cy="btn-change-center"
            >
              {t('action_change_center')}
            </Button>
          </Box>
          <Dialog
            className="center-select popup"
            title={t('select-center')}
            open={open}
            onClose={handleClose}
            {...rest}
          >
            <DialogContent className="popup-grid-items">
              <SearchProvider>
                <div className="wrapper-list-items">
                  <List
                    items={refItems}
                    selectedItem={selectedCenter}
                    element={CenterItem}
                    elementProps={{
                      onClick: (item: Item) => {
                        analyticsEvent({
                          category: 'Booking',
                          action: 'SelectCenters',
                        });
                        onReset();
                        setFieldValue(field.name, item.center.id);
                        dispatch(centersActions.setItem(item));
                        dispatch(practitionersActions.getActions().reset());
                        handleClose();
                      },
                    }}
                  />
                </div>
                <div className="popup-map">
                  <Map
                    items={centers}
                    useSearchContext
                    onItemSelected={setSelectedCenter}
                  />
                </div>
              </SearchProvider>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
};

export default CenterSelect;
